import { useLazyGetProductInfoRequestListQuery } from '@api/productRequest.api';
import {
  ProductInfoItem,
  ProductRequestListFilterKeys,
  ProductRequestListFilterType,
  ProductRequestListFilterValue,
  RequestSorting,
} from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { DropdownOption } from '@components';
import { useAppDispatch, useAppSelector, useAuth } from '@hooks';
import { getTableBlockData, initBlock, setFilters, setOffset } from '@store/table/table.slice';
import { t } from '@translations';
import dayjs from 'dayjs';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

const ITEMS_PER_PAGE = 10;

type ProductInfoRequestContextProps = {
  children: React.ReactNode;
  id: string;
  numberItemsOnPage?: number;
};

type ProductListRequestListType = {
  isWorking: boolean;
  isError: boolean;
  totalCount: number;
  offset: number;
  list: ProductInfoItem[];
  id: string;
  itemsPerPage: number;
  sortOptions: DropdownOption<RequestSorting>[];
  handleFilterChange: ({ filterName, filterValue }: ProductRequestListFilterType) => void;
  filters: ProductRequestListFilterType[];
  getFilterValue: (
    filterName: ProductRequestListFilterKeys
  ) => ProductRequestListFilterValue | undefined;
  clearFilters: () => void;
};

export const ProductInfoRequestListContext = createContext<ProductListRequestListType | null>(null);

export const ProductInfoRequestListProvider = ({
  children,
  id,
  numberItemsOnPage = ITEMS_PER_PAGE,
}: ProductInfoRequestContextProps) => {
  const [trigger, response] = useLazyGetProductInfoRequestListQuery();
  const { isBeremaUser } = useAuth();
  const { isFetching, isLoading, isError, currentData } = response;
  const isWorking = isLoading || isFetching;
  const list = useMemo(() => currentData?.items || [], [currentData?.items]);
  const dispatch = useAppDispatch();
  const totalCount = currentData?.total || 0;

  const { filters, offset } = useAppSelector(getTableBlockData(id)) as {
    filters: ProductRequestListFilterType[];
    offset: number;
  };

  const getFilterValue = useCallback(
    (filterName: string) => {
      return filters?.find((filter) => filter.filterName === filterName)?.filterValue;
    },
    [filters]
  );

  const initialFilters = useMemo(
    () => [
      ...(isBeremaUser
        ? [
            {
              filterName: 'dealerId',
              filterValue: null,
            },
          ]
        : []),
      {
        filterName: 'date',
        filterValue: '',
      },
      {
        filterName: 'status',
        filterValue: '',
      },
      {
        filterName: 'sortBy',
        filterValue: 'Descending',
      },
    ],
    [isBeremaUser]
  );

  const sortOptions = useMemo<DropdownOption<RequestSorting>[]>(
    () => [
      { label: t(TextCodes.ProductRequest.Base.DateAsc), value: 'Ascending' },
      { label: t(TextCodes.ProductRequest.Base.DateDesc), value: 'Descending' },
    ],
    []
  );

  const handleFilterChange = useCallback(
    ({ filterName, filterValue }: ProductRequestListFilterType) => {
      dispatch(
        setFilters({
          id,
          filters: [
            ...filters.filter((filter) => filter.filterName !== filterName),
            {
              filterName,
              filterValue,
            },
          ],
        })
      );
    },
    [filters, id, dispatch]
  );

  const clearFilters = useCallback(() => {
    dispatch(
      setFilters({
        id,
        filters: initialFilters,
      })
    );
    dispatch(setOffset({ id, offset: 0 }));
  }, [dispatch, id, initialFilters]);

  useEffect(() => {
    dispatch(initBlock({ id, filters: initialFilters, visibleFilters: [] }));
  }, [dispatch, id, initialFilters]);

  useEffect(() => {
    const sortValue = getFilterValue('sortBy');
    const dealerId = getFilterValue('dealerId');
    const status = getFilterValue('status');
    const date = getFilterValue('date');
    trigger({
      paging: {
        skip: offset,
        take: numberItemsOnPage,
      },
      ...(dealerId && { dealerId }),
      ...(status && { status }),
      ...(date && { date: dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSSZ') }),
      sortBy: {
        key: 'Timestamp',
        direction: sortValue || 'Descending',
      },
    });
  }, [trigger, offset, numberItemsOnPage, filters, getFilterValue]);

  const contextValue = useMemo(() => {
    return {
      isWorking,
      isError,
      totalCount,
      itemsPerPage: numberItemsOnPage,
      offset,
      list,
      id,
      sortOptions,
      handleFilterChange,
      filters,
      getFilterValue,
      clearFilters,
    };
  }, [
    isWorking,
    list,
    isError,
    id,
    offset,
    totalCount,
    sortOptions,
    numberItemsOnPage,
    handleFilterChange,
    filters,
    getFilterValue,
    clearFilters,
  ]);

  return (
    <ProductInfoRequestListContext.Provider value={contextValue}>
      {children}
    </ProductInfoRequestListContext.Provider>
  );
};

export const useProductInfoRequestListContext = () => {
  const context = useContext(ProductInfoRequestListContext);
  if (!context) {
    throw new Error(
      'ProductInfoRequestListContext must be used within a ProductInfoRequestListProvider'
    );
  }
  return context;
};
